import React, { useEffect, useRef, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { FiArrowLeft, FiCheck, FiCheckCircle, FiCopy, FiExternalLink, FiFile, FiFolder, FiInfo } from 'react-icons/fi';
import { BsFiletypeDocx, BsFiletypePdf } from 'react-icons/bs';
import {
    getDocumentFileType,
    getPartnerDocumentGroups,
    getPartnerName,
    getPartnerS3ConsoleUrl,
    getPartnerS3Info,
} from '../../utils/partnerCreateResult';
import './PartnerCreateSuccess.css';

const FILE_ICONS = {
    pdf: { Icon: BsFiletypePdf, label: 'Fichier PDF' },
    docx: { Icon: BsFiletypeDocx, label: 'Fichier Word' },
    other: { Icon: FiFile, label: 'Fichier' },
};

const CopyButton = ({ value, label }) => {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 2000);
        } catch {
            setCopied(false);
        }
    };

    return (
        <button
            type="button"
            className={`pcs-copy-btn${copied ? ' is-copied' : ''}`}
            onClick={handleCopy}
            aria-label={copied ? `${label} : copié` : label}
            title={copied ? 'Copié' : label}
        >
            {copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
        </button>
    );
};

const DocumentItem = ({ name }) => {
    const { Icon, label } = FILE_ICONS[getDocumentFileType(name)];
    return (
        <li className="pcs-doc">
            <Icon className="pcs-doc__icon" role="img" aria-label={label} />
            <span className="pcs-doc__name">{name}</span>
            <CopyButton value={name} label={`Copier le nom du fichier ${name}`} />
        </li>
    );
};

const S3InfoRow = ({ label, children }) => (
    <div className="pcs-s3__row">
        <dt>{label}</dt>
        <dd>{children}</dd>
    </div>
);

/** Confirmation affichée après la création réussie d'un compte partenaire. */
const PartnerCreateSuccess = ({ result, onBackToList }) => {
    const titleRef = useRef(null);
    const partnerName = getPartnerName(result);
    const consoleUrl = getPartnerS3ConsoleUrl(result);
    const s3Info = getPartnerS3Info(result);
    const documentGroups = getPartnerDocumentGroups(result);
    const hasDocuments = documentGroups.length > 0;

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    return (
        <Card className="partner-create-success-v2 mb-4">
            <Card.Body className="pcs-body">
                <section className="pcs-hero" aria-labelledby="pcs-title">
                    <span className="pcs-hero__icon" aria-hidden="true">
                        <FiCheckCircle />
                    </span>
                    <div className="pcs-hero__content">
                        <p className="pcs-hero__eyebrow">Succès</p>
                        <h2 id="pcs-title" className="pcs-hero__title" tabIndex={-1} ref={titleRef}>
                            Compte partenaire créé avec succès
                        </h2>
                        <p className="pcs-hero__message">
                            Le partenaire{' '}
                            <strong className="pcs-hero__partner">« {partnerName || '—'} »</strong>
                            {' '}a été créé.
                            {hasDocuments && ' Vous pouvez maintenant vérifier et déposer ses documents contractuels dans le dossier S3 associé, si nécessaire.'}
                        </p>
                    </div>
                </section>

                {hasDocuments && (
                    <section className="pcs-docs" aria-labelledby="pcs-docs-title">
                        <h3 id="pcs-docs-title" className="pcs-section-title">
                            <FiFolder aria-hidden="true" className="me-2" />
                            Documents contractuels
                        </h3>

                        <div className="pcs-note" role="note">
                            <FiInfo aria-hidden="true" className="pcs-note__icon" />
                            <p className="mb-0">
                                <strong>À noter :</strong> dépôt requis uniquement si le partenaire est configuré
                                en distribution optionnelle. Les documents ci-dessous sont attendus ; leur présence
                                dans S3 n’est pas vérifiée.
                            </p>
                        </div>

                        <div className="pcs-docs__grid">
                            <div className="pcs-docs__lists">
                                {documentGroups.map((group) => (
                                    <div key={group.locale || 'default'} className="pcs-doc-group">
                                        {group.label && (
                                            <div className="pcs-doc-group__header">
                                                <h4 className="pcs-doc-group__title">
                                                    {group.label}
                                                    <code className="pcs-doc-group__locale">{group.locale}/</code>
                                                </h4>
                                                {group.consoleUrl && (
                                                    <a
                                                        className="pcs-doc-group__link"
                                                        href={group.consoleUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label={`Ouvrir le sous-dossier ${group.locale} dans la console S3 (nouvel onglet)`}
                                                    >
                                                        Ouvrir
                                                        <FiExternalLink aria-hidden="true" className="ms-1" />
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        <ul className="pcs-doc-list">
                                            {group.documents.map((name) => (
                                                <DocumentItem key={name} name={name} />
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            <aside className="pcs-s3" aria-labelledby="pcs-s3-title">
                                <h4 id="pcs-s3-title" className="pcs-s3__title">Dossier S3</h4>
                                <dl className="pcs-s3__list">
                                    {(partnerName || s3Info.label) && (
                                        <S3InfoRow label="Partenaire">{s3Info.label || partnerName}</S3InfoRow>
                                    )}
                                    {s3Info.bucket && (
                                        <S3InfoRow label="Bucket"><code>{s3Info.bucket}</code></S3InfoRow>
                                    )}
                                    {s3Info.prefix && (
                                        <S3InfoRow label="Préfixe"><code>{s3Info.prefix}</code></S3InfoRow>
                                    )}
                                    {s3Info.uri && (
                                        <S3InfoRow label="URI S3">
                                            <span className="pcs-s3__uri">
                                                <code>{s3Info.uri}</code>
                                                <CopyButton value={s3Info.uri} label="Copier l’URI S3" />
                                            </span>
                                        </S3InfoRow>
                                    )}
                                </dl>

                                {consoleUrl ? (
                                    <Button
                                        variant="outline-primary"
                                        className="pcs-s3__open"
                                        href={consoleUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Ouvrir le dossier S3 dans la console AWS (nouvel onglet)"
                                    >
                                        <FiFolder aria-hidden="true" className="me-2" />
                                        Ouvrir le dossier S3
                                        <FiExternalLink aria-hidden="true" className="ms-2" />
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="outline-secondary"
                                            className="pcs-s3__open"
                                            disabled
                                            aria-describedby="pcs-s3-unavailable"
                                        >
                                            <FiFolder aria-hidden="true" className="me-2" />
                                            Ouvrir le dossier S3
                                        </Button>
                                        <p id="pcs-s3-unavailable" className="pcs-s3__hint">
                                            Lien vers la console AWS indisponible.
                                        </p>
                                    </>
                                )}
                            </aside>
                        </div>
                    </section>
                )}

                <div className="pcs-actions">
                    <Button variant="primary" className="pcs-actions__back" onClick={onBackToList}>
                        <FiArrowLeft aria-hidden="true" className="me-2" />
                        Retour à la liste des partenaires
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default PartnerCreateSuccess;
